"use client";
import {
  IconArrowLeft,
  IconArrowRight,
  IconMail,
  IconPhone,
} from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

export const AnimatedTestimonials = ({ testimonials, autoplay = true }) => {
  const [active, setActive] = useState(0);

  const handleNext = () => {
    setActive((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const isActive = (index) => {
    return index === active;
  };

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 10000);
      return () => clearInterval(interval);
    }
  }, [autoplay]);

  const randomRotateY = () => {
    return Math.floor(Math.random() * 21) - 10;
  };
  const extractPlainText = (html) => {
    // Create a temporary div element to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    // Return the plain text content
    return tempDiv.textContent || tempDiv.innerText || "";
  };
  return (
    <div className="max-w-sm md:max-w-4xl mx-auto antialiased font-sans px-4 md:px-8 lg:px-12 mt-10">
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-20">
        <div>
          <div className="relative h-80 w-full">
            <AnimatePresence>
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id} // Use `id` as the key
                  initial={{
                    opacity: 0,
                    scale: 0.9,
                    z: -100,
                    rotate: randomRotateY(),
                  }}
                  animate={{
                    opacity: isActive(index) ? 1 : 0.7,
                    scale: isActive(index) ? 1 : 0.95,
                    z: isActive(index) ? 0 : -100,
                    rotate: isActive(index) ? 0 : randomRotateY(),
                    zIndex: isActive(index)
                      ? 40
                      : testimonials.length + 2 - index,
                    y: isActive(index) ? [0, -80, 0] : 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.9,
                    z: 40,
                    rotate: randomRotateY(),
                  }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 origin-bottom"
                >
                  {testimonial.photo && (
                    <img
                      src={testimonial.photo}
                      alt={testimonial.name}
                      width={500}
                      height={500}
                      draggable={false}
                      className="h-full w-full rounded-3xl object-cover object-center"
                    />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="flex flex-col pt-12 md:pt-0 mt-10 items-center">
            <div className="flex gap-4 justify-center items-center">
              <button
                onClick={handlePrev}
                className="h-7 w-7 md:h-14 md:w-14 rounded-full flex items-center justify-center group/button hover:bg-gray-100 transition-colors duration-300"
              >
                <IconArrowLeft className="h-5 w-5 text-gray-600 group-hover/button:rotate-12 transition-transform duration-300" />
              </button>
              <div className="flex flex-col justify-center items-center gap-3">
                <p className="font-semibold text-gray-700">
                  For more Testimonials
                </p>
                <div className="relative w-56 h-1.5 bg-gray-100 rounded-full overflow-hidden group">
                  {/* Background track with subtle pattern */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200" />
                  
                  {/* Progress bar with modern gradient */}
                  <div 
                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-in-out group-hover:h-2"
                    style={{
                      width: `${((active + 1) / testimonials.length) * 100}%`,
                      background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                    }}
                  >
                    {/* Glowing effect */}
                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                  </div>

                  {/* Modern dots */}
                  <div className="absolute inset-0 flex items-center justify-between px-1">
                    {testimonials.map((_, index) => (
                      <div
                        key={index}
                        className={`w-1 h-1 rounded-full transition-all duration-300 `}
                      />
                    ))}
                  </div>

                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
              <button
                onClick={handleNext}
                className="h-7 w-7 md:h-14 md:w-14 rounded-full flex items-center justify-center group/button hover:bg-gray-100 transition-colors duration-300"
              >
                <IconArrowRight className="h-5 w-5 text-gray-600 group-hover/button:-rotate-12 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-between flex-col py-4">
          <motion.div
            key={active}
            initial={{
              y: 20,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{
              y: -20,
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
          >
            <h3 className="text-2xl font-bold ">{testimonials[active].name}</h3>
            <p className="text-sm">
              {testimonials[active].organization}{" "}
              {/* Use `organization` instead of `designation` */}
            </p>
            <motion.p className="text-lg">
              {extractPlainText(testimonials[active].statement)
                .split(" ")
                .map((word, index) => (
                  <motion.span
                    key={index}
                    initial={{
                      filter: "blur(10px)",
                      opacity: 0,
                      y: 5,
                    }}
                    animate={{
                      filter: "blur(0px)",
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.2,
                      ease: "easeInOut",
                      delay: 0.02 * index,
                    }}
                    className="inline-block"
                  >
                    {word}&nbsp;
                  </motion.span>
                ))}
            </motion.p>

            {/* Add Phone and Email with Icons */}
            <div className="mt-4 space-y-2">
              {testimonials[active]?.contact_phone && (
                <div className="flex items-center gap-2">
                  <IconPhone className="h-5 w-5 text-gray-600" />
                  <p className="text-sm text-gray-600">
                    {testimonials[active].contact_phone}
                  </p>
                </div>
              )}
              {testimonials[active]?.contact_email && (
                <div className="flex items-center gap-2">
                  <IconMail className="h-5 w-5 text-gray-600" />
                  <p className="text-sm text-gray-600">
                    {testimonials[active].contact_email}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

AnimatedTestimonials.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      organization: PropTypes.string.isRequired,
      statement: PropTypes.string.isRequired,
      photo: PropTypes.string,
      contact_phone: PropTypes.string,
      contact_email: PropTypes.string,
    })
  ).isRequired,
  autoplay: PropTypes.bool,
};

AnimatedTestimonials.defaultProps = {
  autoplay: true,
};
